/**
 * h5/src/ws/backoff.ts
 * 指数退避算法（带 jitter，可注入 RNG）
 *
 * 公式：delay(n) = min(base * 2^n, max) * (1 ± 0.2)
 * 参数：base = 1000ms, max = 60000ms, jitter ∈ [-0.2, +0.2]
 *
 * n=0  → ~1000ms
 * n=1  → ~2000ms
 * n=2  → ~4000ms
 * n=3  → ~8000ms
 * n=4  → ~16000ms
 * n=5  → ~32000ms
 * n>=6 → ~60000ms（封顶）
 * n>=15 → Infinity（放弃）
 */

import type { BackoffController, BackoffOptions } from './types'

/**
 * 创建指数退避控制器
 *
 * @example
 * ```ts
 * const backoff = createBackoff()
 * const delay = backoff.next() // 首次约 1000ms
 * if (delay === Infinity) { // 超过 15 次，放弃 }
 * backoff.reset() // 重置计数
 * ```
 */
export function createBackoff(opts?: BackoffOptions): BackoffController {
  const base = opts?.base ?? 1000
  const max = opts?.max ?? 60_000
  const maxAttempts = opts?.maxAttempts ?? 15
  const rng = opts?.rng ?? Math.random

  let _attempts = 0

  return {
    get attempts() {
      return _attempts
    },

    /**
     * 返回下一次重连延迟（ms）
     * 当 attempts >= maxAttempts 时返回 Infinity
     */
    next(): number {
      if (_attempts >= maxAttempts) {
        return Infinity
      }
      // exp = min(base * 2^n, max)
      const exp = Math.min(base * Math.pow(2, _attempts), max)
      // jitter: (1 + [-0.2, +0.2]) = rng * 0.4 + 0.8
      const jitter = rng() * 0.4 + 0.8
      _attempts++
      return Math.floor(exp * jitter)
    },

    /**
     * 重置尝试计数器
     */
    reset(): void {
      _attempts = 0
    },
  }
}
